export const namespaced = true
import GatewayPortalV2 from "@soenergy/frontend-library/src/services/api/GatewayPortalV2"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"

import {
  TIMES_PAYED_TO_RENEW_WITH_CREDIT_IN_MONTHS,
  LAST_READ_THRESHOLD_TO_RENEW_WITH_CREDIT_IN_DAYS,
  LAST_BILL_CREATED_THRESHOLD_TO_RENEW_WITH_CREDIT_IN_DAYS,
  EvFuelType,
  EvRegistrationCheckErrorMessages,
  MeterEligibilityError,
} from "@/constants"
import { IMeter } from "@/types/IMeter"
import dayjs from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import { AxiosError } from "axios"
import { RenewalQuote } from "@/models/RenewalQuote"
import { FuelType } from "@/types/RenewalQuote.types"
import renewalStatusHelper from "@soenergy/frontend-library/src/helpers/renewalStatusHelper"

interface IGroupedAgreement {
  displayName: string
  tariffDurationInMonths: number | null
  hasGas: boolean
  hasElec: boolean
  hasSmartMeter: boolean
  hasOnlySmartMeters: boolean
  isVariable: boolean
  agreements: unknown[]
}

const QuotesFetchErrors = {
  100: "NO_ESTIMATED_USAGE",
  NO_QUOTES: "NO_QUOTES",
  UNKNOWN: "UNKNOWN",
} as const

type QuotesFetchError =
  (typeof QuotesFetchErrors)[keyof typeof QuotesFetchErrors]

dayjs.extend(isSameOrAfter)

const getDefaultState = () => {
  return {
    directDebit: {},
    preferences: {},
    submissionError: false,
    renewalQuotes: [] as RenewalQuote[],
    quotesFetchError: null as null | QuotesFetchError,
    selectedQuote: null,
    isSeasonalPlan: false,
    renewingWithCredit: false,
    isPendingAgreements: false,

    // EV tariff eligibility form
    hasEv: null as null | boolean,
    isConsideringEv: null as null | boolean,
    hasEligibleVehicleForEvTariff: null as null | boolean,
    vehicleFuelType: null as null | keyof typeof EvFuelType,
    isMeterEligibleForEvTariff: null as null | boolean,
    isEvTariffEligibilityChecked: false,
  }
}

const sortRenewalQuotes = (renewalQuotes: RenewalQuote[]) => {
  const recommendedQuotes = renewalQuotes
    .filter((quote) => quote.isRecommended)
    .sort((a, b) => a.costPerYearInPounds - b.costPerYearInPounds)

  const otherQuotes = renewalQuotes
    .filter((quote) => !quote.isRecommended)
    .sort((a, b) => a.costPerYearInPounds - b.costPerYearInPounds)

  return [...recommendedQuotes, ...otherQuotes]
}

export const state = getDefaultState()

export const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  SET_SELECTED_QUOTE(state, selectedQuote) {
    state.selectedQuote = selectedQuote
  },
  SET_SEASONAL_PLAN(state, isSeasonalPlan) {
    state.isSeasonalPlan = isSeasonalPlan
  },
  SET_IS_RENEWING_WITH_CREDIT(state, renewingWithCredit) {
    state.renewingWithCredit = renewingWithCredit
  },
  SET_PREFERENCES(state, preferencesObject) {
    state.preferences = Object.assign({}, state.preferences, preferencesObject)
  },
  SET_IS_EV_TARIFF_ELIGIBILITY_CHECKED(state, isEvTariffEligibilityChecked) {
    state.isEvTariffEligibilityChecked = isEvTariffEligibilityChecked
  },
  SET_RENEWAL_QUOTES(state, quotes: RenewalQuote[]) {
    state.renewalQuotes = quotes
  },
  SET_QUOTES_FETCH_ERROR(state, error) {
    state.quotesFetchError = error
  },
  SET_DIRECT_DEBIT(state, directDebit) {
    state.directDebit = Object.assign({}, state.directDebit, directDebit)
  },
  SET_SUBMISSION_ERROR(state, submissionError) {
    state.submissionError = submissionError
  },
  PENDING_RENEW_AGREEMENTS(state, isPending) {
    state.isPendingAgreements = isPending
  },
  SET_HAS_EV(state, hasEv) {
    state.hasEv = hasEv
  },
  SET_VEHICLE_FUEL_TYPE(state, vehicleFuelType) {
    state.vehicleFuelType = vehicleFuelType
  },
  SET_HAS_ELIGIBLE_VEHICLE_FOR_EV_TARIFF(state, hasEligibleVehicleForEvTariff) {
    state.hasEligibleVehicleForEvTariff = hasEligibleVehicleForEvTariff
  },
  SET_IS_CONSIDERING_EV(state, isConsideringEv) {
    state.isConsideringEv = isConsideringEv
  },
  SET_IS_METER_ELIGIBLE_FOR_EV_TARIFF(state, isMeterEligibleForEvTariff) {
    state.isMeterEligibleForEvTariff = isMeterEligibleForEvTariff
  },
}

export const actions = {
  async fetchRenewalQuotes(
    { commit, rootState, rootGetters },
    { generateQuotesForEvTariffs } = { generateQuotesForEvTariffs: false }
  ) {
    commit("SET_QUOTES_FETCH_ERROR", null)

    try {
      const { data } = await GatewayPortalV2.getRenewalQuotes(
        rootState.currentAccount,
        { generateQuotesForEvTariffs }
      )

      if (!data?.length) throw new Error(QuotesFetchErrors.NO_QUOTES)

      const accountBalance = -rootGetters.account.balance
      const quotes = data.map(
        (quote) => new RenewalQuote(quote, accountBalance)
      )

      commit("SET_RENEWAL_QUOTES", quotes)
    } catch (error) {
      console.error(error)
      ErrorReporter.report(error)
      let errorCode: string = QuotesFetchErrors.UNKNOWN
      if (error instanceof AxiosError) {
        errorCode = error?.response?.data?.errorCode
      } else if (error instanceof Error) {
        errorCode = error.message
      }

      commit(
        "SET_QUOTES_FETCH_ERROR",
        QuotesFetchErrors[errorCode] ?? QuotesFetchErrors.UNKNOWN
      )
    }
  },
  updatePreferences({ commit }, preferences) {
    commit("SET_PREFERENCES", preferences)
  },
  updateDirectDebit({ commit }, directDebit) {
    commit("SET_DIRECT_DEBIT", directDebit)
  },
  async setPreferences({ dispatch, rootGetters, rootState }) {
    await dispatch("user/fetchMeterReadReminderDetails", null, { root: true })
    const marketing = !rootState.user.customer.marketingOptOutFl

    dispatch("updatePreferences", {
      meterReadReminder: rootGetters["user/meterReadReminderEnabled"],
      showMeterReadReminder:
        !rootGetters["user/meterReadReminderEnabled"] &&
        rootGetters["meter/hasLegacyMeter"],
      marketing: marketing,
      showMarketing: !marketing,
    })
  },

  async verifyVehicleForEvTariffEligibility(
    { commit, dispatch },
    carReg: string
  ) {
    const { data } = await GatewayPortalV2.verifyEvVehicle(carReg)
    const vehicleFuelType = data?.fuelType
    const isEligible = data?.isEv
    commit("SET_HAS_ELIGIBLE_VEHICLE_FOR_EV_TARIFF", isEligible)
    dispatch("updateVehicleFuelType", vehicleFuelType)
    if (!isEligible) {
      throw new Error(EvRegistrationCheckErrorMessages.NoValidEvFoundError)
    }
    return { vehicleFuelType }
  },

  async verifyMeterForEvTariffEligibility({ commit, rootGetters }) {
    const mpan = rootGetters["smbp/mpan"]
    if (!mpan) throw new Error(MeterEligibilityError.NO_METER_POINT_FOUND)

    const { data } = await GatewayPortalV2.verifySmartMeter(mpan)
    const isMeterEligible = !!data?.isEligible
    commit("SET_IS_METER_ELIGIBLE_FOR_EV_TARIFF", isMeterEligible)
    if (data?.errorCode) throw new Error(data.errorCode)
    return { isMeterEligible }
  },

  async updateVehicleFuelType({ commit, rootState }, vehicleFuelType: string) {
    commit("SET_VEHICLE_FUEL_TYPE", vehicleFuelType)

    const customerId = rootState.user.customer.id
    const fuelType = EvFuelType[vehicleFuelType]

    try {
      if (!fuelType) throw new Error("Invalid vehicle fuel type")
      await GatewayPortalV2.updateCustomerDetails(customerId, { fuelType })
    } catch (e) {
      console.error(e)
      ErrorReporter.report(e)
    }
  },
}

export const getters = {
  hasPendingRenewal(state, getters, rootState, rootGetters) {
    return renewalStatusHelper.hasPendingRenewal(
      rootState.user,
      rootGetters.account?.number
    )
  },
  hasRenewedToday(state, getters, rootState, rootGetters) {
    const today = dayjs()
    return !!rootGetters.agreements?.some((agreement) => {
      return (
        dayjs(agreement.orderedDt).isSame(today, "day") && !agreement.cancelled
      )
    })
  },
  isEligibleForRenewal(state, getters, rootState, rootGetters) {
    const anyAgreementIsOutOfContract =
      rootGetters["agreements/anyAgreementIsOutOfContract"]

    return (
      getters.hasAgreementsEligibleForRenewal &&
      (!getters.hasRenewedToday || anyAgreementIsOutOfContract) &&
      !getters.hasPendingRenewal
    )
  },
  getIsSeasonalPlan(state) {
    return state.isSeasonalPlan
  },
  getRenewingWithCredit(state) {
    return state.renewingWithCredit
  },
  getSelectedQuote(state) {
    return state.selectedQuote
  },
  getPreferences(state) {
    return state.preferences
  },
  getDirectDebit(state) {
    return state.directDebit
  },
  getRenewalQuotes(state) {
    return sortRenewalQuotes(state.renewalQuotes)
  },
  getFixedQuoteTariffName(state) {
    return state.renewalQuotes.find(({ isFixedRate }) => isFixedRate)
      ?.tariffNameFormatted
  },
  isEligibleForEvTariff(state) {
    return (
      state.hasEv &&
      state.isMeterEligibleForEvTariff &&
      state.hasEligibleVehicleForEvTariff
    )
  },
  isEligibleToUseCredit(state, getters, rootState, rootGetters) {
    const allCurrentMeters = Object.values(
      rootGetters["meters/allCurrent"]
    ).flat(1) as IMeter[]

    const allRenewMetersHadRealReadWithinPeriod =
      getters.getRenewableAgreements.every((meterpoint) => {
        return allCurrentMeters
          .filter(
            (meter) =>
              meter.meterpointIdentifier === meterpoint.meterpointIdentifier
          )
          .every((meter) => {
            return dayjs(meter.lastReadDate).isSameOrAfter(
              dayjs().subtract(
                LAST_READ_THRESHOLD_TO_RENEW_WITH_CREDIT_IN_DAYS,
                "day"
              )
            )
          })
      })

    const lastBillCreatedWithinPeriod = dayjs(
      rootGetters["bills/latestBillCreatedDate"]
    ).isSameOrAfter(
      dayjs().subtract(
        LAST_BILL_CREATED_THRESHOLD_TO_RENEW_WITH_CREDIT_IN_DAYS,
        "day"
      )
    )

    const hasMinimumSuccessfulMonthlyPayments =
      rootGetters.payments.filter(
        (payment) => payment.requestStatus === "Successful"
      ).length >= TIMES_PAYED_TO_RENEW_WITH_CREDIT_IN_MONTHS

    return (
      allRenewMetersHadRealReadWithinPeriod &&
      lastBillCreatedWithinPeriod &&
      hasMinimumSuccessfulMonthlyPayments
    )
  },
  anyQuoteIsMissingConsumption(state, getters) {
    return getters.getRenewalQuotes.some((quote) => quote.isMissingConsumption)
  },
  allRenewableAgreementsHaveValidConsumption(state, getters) {
    return getters.getRenewableAgreements.every(
      (agreement) => !!agreement.costPerYearInPounds
    )
  },
  hasAgreementsEligibleForRenewal(state, getters) {
    return getters.getRenewableAgreements.length > 0
  },
  hasElecMeterpointEligibleForRenewal(state, getters) {
    return getters.getRenewableAgreements.some(
      (agreement) => agreement.fuelType === FuelType.Electricity
    )
  },
  nextPaymentDateAfterRenewal(state, getters, rootState, rootGetters) {
    const currentPaymentSchedule =
      rootGetters["payments/currentPaymentSchedule"]

    return currentPaymentSchedule
      ? dayjs(currentPaymentSchedule.nextPaymentDt)
      : dayjs().add(14, "days")
  },
  getRenewableAgreements(state, getters, rootState, rootGetters) {
    const meterpointAgreements =
      rootGetters["meterpointAgreements/getActiveAgreements"]
    return meterpointAgreements.filter((agreement) => agreement.canRenew)
  },
  getCurrentAgreementsForRenewal(state, getters, rootState, rootGetters) {
    const meterpoints = rootGetters["meters/getMeterPoints"]
    const renewableAgreements = getters.getRenewableAgreements
    const groupedAgreements: IGroupedAgreement[] = []

    // Join agreements with meter points
    let agreementsArray = renewableAgreements.map((agreement) => {
      const meterpoint = meterpoints.find(
        (meterpoint) => meterpoint.identifier === agreement.meterpointIdentifier
      )
      return { ...agreement, meterpoint }
    })

    // Group agrrements by tariff name
    while (agreementsArray.length > 0) {
      const agreement = agreementsArray[0]
      const agreementGroup = agreementsArray.filter(
        (a) =>
          agreement.displayName === a.displayName &&
          agreement.tariffDurationInMonths === a.tariffDurationInMonths
      )

      groupedAgreements.push({
        displayName: agreement.displayName ?? "Unknown",
        tariffDurationInMonths: agreement.tariffDurationInMonths,
        hasGas: agreementGroup.some((a) => a.fuelType === "Gas"),
        hasElec: agreementGroup.some((a) => a.fuelType === "Electricity"),
        hasSmartMeter: agreementGroup.some((a) => a.meterpoint.hasSmartMeter),
        hasOnlySmartMeters: agreementGroup.every(
          (a) => a.meterpoint?.hasOnlySmartMeters
        ),
        isVariable: agreementGroup.every((a) => a.isTariffVariable),
        agreements: agreementGroup,
      })

      // Remove agreements that have been grouped from the list
      agreementsArray = agreementsArray.filter(
        (a) => !agreementGroup.includes(a)
      )
    }

    const annualCostInPounds = renewableAgreements.reduce(
      (total, a) => total + a.costPerYearInPounds,
      0
    )

    const costPerMonthInPounds = renewableAgreements.reduce(
      (total, a) => total + a.costPerMonthInPounds,
      0
    )

    const isSingleTariff = groupedAgreements.length === 1
    const hasOutOfSyncAgreements = renewableAgreements.some(
      (a) => a.startDate !== renewableAgreements[0].startDate
    )

    return {
      groupedAgreements,
      annualCostInPounds,
      costPerMonthInPounds,
      hasComplexCurrentAgreements: !isSingleTariff || hasOutOfSyncAgreements,
      isSingleTariff,
      hasOutOfSyncAgreements,
    }
  },
}
